import { memo, useMemo, useState } from "react";

// React Query
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { Grid, Skeleton, Stack, Typography } from "@mui/material";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";

// Queries
import { getCallActivity, uploadCallActivity } from "@/DataPages/api/callActivity/queries";

// Constants
import actionsList from "@Constants/actionsList";

// Components
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import UploadDataWithTimerButton from "@Layouts/components/Buttons/UploadDataWithTimerButton";
import CallActivityTable from "@/DataPages/components/CallActivity/components/Tables/CallActivityReportTable";
import { errorLogger } from "@/Common/helpers/logger";
import { ICallActivity, IUpdateCallActivityResponse } from "@/DataPages/types/callActivity/model";
import { ISidebarItem } from "@Common/interfaces/sidebar";

const FILE_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const PAGINATION_SIZE = 2000;
const CallActivityReportTable = memo(CallActivityTable);

interface ICallActivityModelProps {
	locationId: string | null | undefined;
	selected: ISidebarItem | null;
	userId?: string;
	isUpdatable?: boolean;
}

export default function CallActivityModel(props: Readonly<ICallActivityModelProps>) {
	const { locationId, selected, userId, isUpdatable } = props;
	const [ingesting, setIngesting] = useState(false);
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();
	const UPLOAD_CALL_ACTIVITY_REPORT = useAuth(actionsList.UPLOAD_CALL_ACTIVITY_REPORT);
	const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useInfiniteQuery({
		queryKey: ["callActivityCache", locationId, selected?._id],
		queryFn: async ({ pageParam }) => {
			if (!locationId || !selected?._id) {
				return { activities: [] as ICallActivity[], hasNext: false };
			}

			const paginatedResponse = await getCallActivity(locationId, selected?._id, userId ?? "", pageParam, PAGINATION_SIZE);

			if (!paginatedResponse) {
				throw new Error("Error retrieving Call Activity Report");
			}

			const { callActivities: activities, hasNext } = paginatedResponse;

			activities.forEach((entry) => {
				entry.isUsable = entry.queueEvaluation.isUsable || (entry.individualEvaluation.isUsable && entry.isValidCallDate);
			});

			return { activities, hasNext };
		},
		enabled: !!locationId && !!selected?._id,
		gcTime: 5000,
		staleTime: Infinity,
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			const activitiesLength = allPages.flatMap((page) => page.activities).length;
			return lastPage.hasNext ? activitiesLength : null;
		},
	});

	const callActivityReport = useMemo(() => {
		return data?.pages.flatMap((page) => page.activities) ?? [];
	}, [data]);

	if (hasNextPage && !isFetching && !isLoading) {
		fetchNextPage({ cancelRefetch: false });
	}

	const { getSubscription } = useSubscriptions();
	const callActivitySubscription = getSubscription(["callActivityReportSubscription", locationId, selected?._id]);
	callActivitySubscription?.subscriptions?.onUpdateCallActivity?.setCallback((result: IUpdateCallActivityResponse) => {
		const callActivityResponse = result?.data?.onUpdateCallActivity;
		// invalidate cache to force retrieval
		queryClient.resetQueries({ queryKey: ["callActivityCache", locationId, callActivityResponse?.commissionCycleId] });
	});

	const handleUploadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
		try {
			const file = event.target.files ? event.target.files[0] : null;

			if (!file) {
				return;
			}
			if (file.type !== FILE_TYPE) {
				return;
			}
			if (!locationId || !selected?._id) {
				return;
			}
			const upload = uploadCallActivity(locationId, selected._id, file);
			upload.then(() => {
				event.target.value = "";
				setIngesting(false);
			});
			setIngesting(true);
		} catch (error) {
			errorLogger(error as Error, { function: "handleUploadFile", message: "Error uploading call activity report", props });
		}
	};

	return (
		<Grid container alignContent={"flex-start"} sx={{ height: "100%" }} pb={4}>
			<Grid item xs={12}>
				{isLoading ? (
					<Stack direction={"row"} justifyContent={"space-between"}>
						<Skeleton variant="rectangular" height={25} width={350} sx={{ borderRadius: "6px" }} />
						{isUpdatable && <Skeleton variant="rectangular" height={30} width={120} sx={{ borderRadius: "6px" }} />}
					</Stack>
				) : (
					<>
						{selected && (
							<Stack direction={"row"} justifyContent={"space-between"}>
								<Typography className={"page-title"}>{`${selected?.title} - Call Activity Report`}</Typography>
								{isUpdatable && (
									<UploadDataWithTimerButton
										title={"Upload Report"}
										className={"upload-file-btn medium-blue-btn btn-rounded chargebacks-upload-btn"}
										accept=".xlsx"
										disabled={!UPLOAD_CALL_ACTIVITY_REPORT}
										uploading={ingesting}
										onChange={handleUploadFile}
										sx={{ padding: "6px 18px", minWidth: "120px" }}
									/>
								)}
							</Stack>
						)}
					</>
				)}
			</Grid>
			<Grid item xs={12} height={600}>
				{isLoading ? (
					<Grid item xs={12} mt={1}>
						<TableLoader toolbar buttons={3} content rows={5} multiTitles={5} />
					</Grid>
				) : (
					<CallActivityReportTable data={callActivityReport ?? []} hasNextPage={hasNextPage} />
				)}
			</Grid>
		</Grid>
	);
}
